var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Card, Table, Button, Space, Tooltip, Popover, Checkbox, Divider, } from "antd";
import { SettingOutlined, EditOutlined, DeleteOutlined, PlusOutlined, ReloadOutlined, UnlockOutlined, LockOutlined, } from "@ant-design/icons";
import { Resizable } from "react-resizable";
import "react-resizable/css/styles.css";
// 在组件顶部添加配置常量
var MIN_COLUMN_WIDTH = 60; // 最小列宽
var MAX_COLUMN_WIDTH = 600; // 最大列宽
// 图标映射
var iconMap = {
    PlusOutlined: React.createElement(PlusOutlined),
    ReloadOutlined: React.createElement(ReloadOutlined),
    UnlockOutlined: React.createElement(UnlockOutlined),
    LockOutlined: React.createElement(LockOutlined),
    DeleteOutlined: React.createElement(DeleteOutlined),
    EditOutlined: React.createElement(EditOutlined),
    SettingOutlined: React.createElement(SettingOutlined),
};
// 添加ResizableTitle组件
var ResizableTitle = function (props) {
    var onResize = props.onResize, width = props.width, restProps = __rest(props, ["onResize", "width"]);
    if (!width) {
        return _jsx("th", __assign({}, restProps));
    }
    return (_jsx(Resizable, __assign({ width: width, height: 0, onResize: onResize, draggableOpts: { enableUserSelectHack: false }, 
        // 添加最小最大宽度限制
        minConstraints: [MIN_COLUMN_WIDTH, 0], maxConstraints: [MAX_COLUMN_WIDTH, 0], handle: _jsx("span", { className: "react-resizable-handle", onClick: function (e) {
                e.stopPropagation();
                e.preventDefault();
            }, style: {
                position: "absolute",
                right: 0,
                top: 0,
                bottom: 0,
                width: 8,
                cursor: "col-resize",
                zIndex: 100,
            } }) }, { children: _jsx("th", __assign({}, restProps, { style: __assign(__assign({}, restProps.style), { position: "relative" }) })) })));
};
/**
 * 通用表格面板组件
 * 包含标题、按钮、表格和分页
 */
function GenericPanelTable(_a) {
    var _b, _c;
    var title = _a.title, _d = _a.buttons, buttons = _d === void 0 ? [] : _d, tableProps = _a.tableProps, _e = _a.loading, loading = _e === void 0 ? false : _e, _f = _a.refreshData, refreshData = _f === void 0 ? function () { } : _f, _g = _a.showSelection, showSelection = _g === void 0 ? false : _g, _h = _a.cardProps, cardProps = _h === void 0 ? {} : _h, extra = _a.extra, pagination = _a.pagination, onPaginationChange = _a.onPaginationChange, _j = _a.defaultPageSize, defaultPageSize = _j === void 0 ? 10 : _j, _k = _a.showColumnSetting, showColumnSetting = _k === void 0 ? true : _k, storageKey = _a.storageKey, _l = _a.resizableColumns, resizableColumns = _l === void 0 ? false : _l, _m = _a.saveColumnWidths, saveColumnWidths = _m === void 0 ? true : _m, defaultActions = _a.defaultActions, headerPadding = _a.headerPadding;
    var _o = useState([]), selectedRowKeys = _o[0], setSelectedRowKeys = _o[1];
    var _p = useState([]), selectedRows = _p[0], setSelectedRows = _p[1];
    var _q = useState(false), popoverVisible = _q[0], setPopoverVisible = _q[1];
    // 内部分页状态（当没有传入分页时使用）
    var _r = useState({
        current: 1,
        pageSize: defaultPageSize,
        total: 0,
    }), internalPagination = _r[0], setInternalPagination = _r[1];
    // 添加列宽状态
    var _s = useState({}), columnWidths = _s[0], setColumnWidths = _s[1];
    // 列可见性状态
    var _t = useState({}), columnVisibility = _t[0], setColumnVisibility = _t[1];
    // 使用外部传入的分页或内部分页
    // 如果 tableProps.pagination 明确设置为 false，则不显示分页
    var currentPagination = tableProps.pagination === false ? false : pagination || internalPagination;
    // 获取表格所有列
    var allColumns = useMemo(function () { return tableProps.columns || []; }, [tableProps.columns]);
    // 存储键，优先使用传入值，否则使用标题（转为字符串）或默认值
    var actualStorageKey = useMemo(function () {
        return storageKey ||
            (typeof title === "string"
                ? "table_columns_".concat(title)
                : "table_columns_default");
    }, [storageKey, title]);
    // 为每列生成唯一标识
    var getColumnKey = useCallback(function (column) {
        var _a;
        return ((_a = (column.dataIndex || column.key || column.title)) === null || _a === void 0 ? void 0 : _a.toString()) || "";
    }, []);
    // 过滤显示的列 - 只定义一次
    var visibleColumns = allColumns.filter(function (column) {
        var key = getColumnKey(column);
        return !key || columnVisibility[key] !== false;
    });
    // 初始化列宽
    useEffect(function () {
        if (resizableColumns && saveColumnWidths) {
            try {
                var savedWidths = localStorage.getItem("".concat(actualStorageKey, "_widths"));
                if (savedWidths) {
                    setColumnWidths(JSON.parse(savedWidths));
                }
            }
            catch (error) {
                console.error("加载列宽配置失败:", error);
            }
        }
    }, [resizableColumns, saveColumnWidths, actualStorageKey]);
    // 保存列宽到本地存储
    useEffect(function () {
        if (resizableColumns &&
            saveColumnWidths &&
            Object.keys(columnWidths).length > 0) {
            try {
                localStorage.setItem("".concat(actualStorageKey, "_widths"), JSON.stringify(columnWidths));
            }
            catch (error) {
                console.error("保存列宽配置失败:", error);
            }
        }
    }, [columnWidths, resizableColumns, saveColumnWidths, actualStorageKey]);
    // 处理列宽调整
    var handleResize = function (index) {
        return function (e, _a) {
            var size = _a.size;
            var newColumnWidths = __assign({}, columnWidths);
            var column = visibleColumns[index];
            var key = getColumnKey(column);
            if (key) {
                // 添加额外的宽度限制逻辑
                var newWidth = size.width;
                // 确保宽度在允许的范围内
                if (newWidth < MIN_COLUMN_WIDTH)
                    newWidth = MIN_COLUMN_WIDTH;
                if (newWidth > MAX_COLUMN_WIDTH)
                    newWidth = MAX_COLUMN_WIDTH;
                newColumnWidths[key] = newWidth;
                setColumnWidths(newColumnWidths);
            }
        };
    };
    // 处理表格组件
    var components = resizableColumns
        ? {
            header: {
                cell: ResizableTitle,
            },
        }
        : undefined;
    // 添加列宽重置函数
    var resetColumnWidths = function () {
        setColumnWidths({});
        try {
            localStorage.removeItem("".concat(actualStorageKey, "_widths"));
        }
        catch (error) {
            console.error("移除列宽配置失败:", error);
        }
        setPopoverVisible(false);
        // 不刷新页面，而是重新进行认证检查
        // setTimeout(() => {
        //   window.location.reload();
        // }, 100);
    };
    // 设置列宽和处理函数
    var resizedColumns = visibleColumns.map(function (col, index) {
        var key = getColumnKey(col);
        // 修改此处逻辑，避免默认宽度过大
        var savedWidth = key && columnWidths[key] ? columnWidths[key] : undefined;
        // 仅当有保存的宽度时才使用，否则使用列自身的宽度或默认最小宽度
        var width = savedWidth || col.width || MIN_COLUMN_WIDTH;
        return __assign(__assign({}, col), { width: width, onHeaderCell: function (column) { return ({
                width: width,
                onResize: handleResize(index),
            }); } });
    });
    // 初始化列可见性
    useEffect(function () {
        // 尝试从本地存储加载配置
        try {
            var savedConfig = localStorage.getItem(actualStorageKey);
            if (savedConfig) {
                // 解析保存的配置
                var parsedConfig = JSON.parse(savedConfig);
                setColumnVisibility(parsedConfig);
            }
            else {
                // 没有保存的配置，初始化为全部可见
                var initialVisibility_1 = {};
                allColumns.forEach(function (column) {
                    var key = getColumnKey(column);
                    if (key) {
                        initialVisibility_1[key] = true;
                    }
                });
                setColumnVisibility(initialVisibility_1);
            }
        }
        catch (error) {
            console.error("加载列配置失败:", error);
            // 出错时，默认所有列可见
            var defaultVisibility_1 = {};
            allColumns.forEach(function (column) {
                var key = getColumnKey(column);
                if (key) {
                    defaultVisibility_1[key] = true;
                }
            });
            setColumnVisibility(defaultVisibility_1);
        }
    }, [actualStorageKey, allColumns, getColumnKey]);
    // 保存列可见性到本地存储
    useEffect(function () {
        // 仅当columnVisibility不为空对象时保存
        if (Object.keys(columnVisibility).length > 0) {
            try {
                localStorage.setItem(actualStorageKey, JSON.stringify(columnVisibility));
            }
            catch (error) {
                console.error("保存列配置失败:", error);
            }
        }
    }, [columnVisibility, actualStorageKey]);
    // 处理列可见性变化
    var handleColumnVisibilityChange = function (checkedValues) {
        var newVisibility = {};
        // 先将所有列设为不可见
        allColumns.forEach(function (column) {
            var key = getColumnKey(column);
            if (key) {
                newVisibility[key] = false;
            }
        });
        // 再将选中的列设为可见
        checkedValues.forEach(function (value) {
            newVisibility[value] = true;
        });
        setColumnVisibility(newVisibility);
    };
    // 重置列可见性，恢复所有列显示
    var resetColumnVisibility = function () {
        var resetVisibility = {};
        allColumns.forEach(function (column) {
            var key = getColumnKey(column);
            if (key) {
                resetVisibility[key] = true;
            }
        });
        setColumnVisibility(resetVisibility);
        setPopoverVisible(false);
    };
    // 处理表格选择变化
    var handleSelectionChange = function (newSelectedRowKeys, newSelectedRows) {
        setSelectedRowKeys(newSelectedRowKeys);
        setSelectedRows(newSelectedRows);
    };
    // 处理表格变化
    var handleTableChange = function (newPagination, filters, sorter, extra) {
        // 如果有外部传入的回调，则调用
        if (onPaginationChange) {
            onPaginationChange(newPagination);
        }
        else {
            // 否则使用内部状态
            setInternalPagination(newPagination);
        }
        // 确保传递所有参数
        if (tableProps.onChange) {
            tableProps.onChange(newPagination, filters, sorter, extra);
        }
    };
    // 列设置弹窗内容
    var columnSettingContent = (_jsxs("div", __assign({ style: { maxWidth: 300 } }, { children: [_jsxs("div", __assign({ style: {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 8,
                } }, { children: [_jsx("span", __assign({ style: { fontWeight: "bold" } }, { children: "\u663E\u793A\u7684\u5217" })), _jsxs(Space, { children: [_jsx(Button, __assign({ type: "link", size: "small", onClick: resetColumnVisibility, title: "\u91CD\u7F6E\u5217\u663E\u793A", style: { fontSize: "12px" } }, { children: "\u91CD\u7F6E\u663E\u793A" })), _jsx(Button, __assign({ type: "link", size: "small", onClick: resetColumnWidths, title: "\u91CD\u7F6E\u5217\u5BBD", danger: true, style: { fontSize: "12px" } }, { children: "\u91CD\u7F6E\u5BBD\u5EA6" }))] })] })), _jsx(Divider, { style: { margin: "8px 0" } }), _jsx(Checkbox.Group, __assign({ style: { display: "flex", flexDirection: "column", gap: "8px" }, value: Object.entries(columnVisibility)
                    .filter(function (_a) {
                    var _ = _a[0], visible = _a[1];
                    return visible;
                })
                    .map(function (_a) {
                    var key = _a[0];
                    return key;
                }), onChange: handleColumnVisibilityChange }, { children: allColumns.map(function (column) {
                    var key = getColumnKey(column);
                    if (!key)
                        return null;
                    var title = typeof column.title === "string" ? column.title : "\u5217 ".concat(key);
                    return (_jsx(Checkbox, __assign({ value: key }, { children: title }), key));
                }) }))] })));
    // 渲染按钮组
    var renderButtonGroup = function (groupType) {
        var groupButtons = buttons.filter(function (button) { return button.group === groupType; });
        return (_jsx(Space, { children: groupButtons.map(function (button) {
                // 检查按钮是否应该隐藏
                var isHidden = typeof button.hidden === "function"
                    ? button.hidden(selectedRows)
                    : button.hidden;
                if (isHidden)
                    return null;
                // 检查按钮是否应该禁用
                var isDisabled = typeof button.disabled === "function"
                    ? button.disabled(selectedRows)
                    : button.disabled;
                // 处理图标：如果是字符串则从映射中获取，否则直接使用
                var iconElement = typeof button.icon === "string"
                    ? iconMap[button.icon] || button.icon
                    : button.icon;
                return (_jsx(Button, __assign({ type: button.type === "danger-border" ? "default" : button.type, icon: iconElement, disabled: isDisabled, size: "small", style: __assign(__assign(__assign(__assign({ color: isDisabled
                            ? "rgba(0, 0, 0, 0.25)"
                            : button.type === "primary"
                                ? "#fff"
                                : button.type === "danger-border"
                                    ? "#ff4d4f"
                                    : "#1f1f1f" }, (button.type === "text" && !isDisabled && { color: "#666" })), (button.type === "primary" &&
                        !isDisabled && {
                        backgroundColor: "#1890ff",
                        borderColor: "#1890ff",
                    })), (button.type === "danger-border" &&
                        !isDisabled && {
                        backgroundColor: "transparent",
                        borderColor: "#ff4d4f",
                        borderWidth: "1px",
                        borderStyle: "solid",
                    })), { fontSize: "12px" }), onClick: function () { return button.onClick(selectedRows, refreshData); }, ghost: button.ghost, danger: button.danger }, { children: button.text }), button.key));
            }) }));
    };
    // 渲染所有按钮分组
    var renderButtons = function () {
        var primaryButtons = buttons.filter(function (button) { return button.group === "primary"; });
        var secondaryButtons = buttons.filter(function (button) { return button.group === "secondary"; });
        var tertiaryButtons = buttons.filter(function (button) { return button.group === "tertiary"; });
        var ungroupedButtons = buttons.filter(function (button) { return !button.group; });
        return (_jsxs("div", __assign({ style: { display: "flex", alignItems: "center", gap: "24px" } }, { children: [tertiaryButtons.length > 0 && renderButtonGroup("tertiary"), secondaryButtons.length > 0 && renderButtonGroup("secondary"), primaryButtons.length > 0 && renderButtonGroup("primary"), ungroupedButtons.length > 0 && (_jsx(Space, { children: ungroupedButtons.map(function (button) {
                        var isHidden = typeof button.hidden === "function"
                            ? button.hidden(selectedRows)
                            : button.hidden;
                        if (isHidden)
                            return null;
                        var isDisabled = typeof button.disabled === "function"
                            ? button.disabled(selectedRows)
                            : button.disabled;
                        // 处理图标：如果是字符串则从映射中获取，否则直接使用
                        var iconElement = typeof button.icon === "string"
                            ? iconMap[button.icon] || button.icon
                            : button.icon;
                        return (_jsx(Button, __assign({ type: button.type === "danger-border" ? "default" : button.type, icon: iconElement, disabled: isDisabled, size: "small", style: __assign(__assign(__assign(__assign({ color: isDisabled
                                    ? "rgba(0, 0, 0, 0.25)"
                                    : button.type === "primary"
                                        ? "#fff"
                                        : button.type === "danger-border"
                                            ? "#ff4d4f"
                                            : "#1f1f1f" }, (button.type === "text" &&
                                !isDisabled && { color: "#666" })), (button.type === "primary" &&
                                !isDisabled && {
                                backgroundColor: "#1890ff",
                                borderColor: "#1890ff",
                            })), (button.type === "danger-border" &&
                                !isDisabled && {
                                backgroundColor: "transparent",
                                borderColor: "#ff4d4f",
                                borderWidth: "1px",
                                borderStyle: "solid",
                            })), { fontSize: "12px" }), onClick: function () { return button.onClick(selectedRows, refreshData); }, ghost: button.ghost, danger: button.danger }, { children: button.text }), button.key));
                    }) }))] })));
    };
    // 修改 titleWithSettings 组件中的 Popover 配置
    var titleWithSettings = (_jsxs(Space, { children: [_jsx("span", __assign({ style: { color: "#1f1f1f", fontWeight: 500 } }, { children: title })), showColumnSetting && (_jsx(Tooltip, __assign({ title: "\u5217\u8BBE\u7F6E" }, { children: _jsx(Popover, __assign({ content: columnSettingContent, title: null, trigger: "click", open: popoverVisible, onOpenChange: setPopoverVisible, placement: "rightTop" // 修改为 rightTop，使弹出框出现在右侧
                    , overlayStyle: { minWidth: "60px" } }, { children: _jsx(Button, { icon: React.createElement(SettingOutlined), type: "text", size: "small", style: { color: "#666" }, onClick: function (e) {
                            e.stopPropagation();
                            setPopoverVisible(!popoverVisible);
                        } }) })) })))] }));
    // 创建组合的extra内容，不再包含列设置按钮
    var combinedExtra = (_jsxs(Space, { children: [renderButtons(), extra] }));
    // 创建缺省操作列
    var createDefaultActionColumn = function () {
        if (!defaultActions)
            return null;
        var onEdit = defaultActions.onEdit, onDelete = defaultActions.onDelete, onTest = defaultActions.onTest, editIcon = defaultActions.editIcon, deleteIcon = defaultActions.deleteIcon, testIcon = defaultActions.testIcon, _a = defaultActions.width, width = _a === void 0 ? 120 : _a, _b = defaultActions.spacing, spacing = _b === void 0 ? 16 : _b;
        // 如果没有提供任何操作回调，则不创建操作列
        if (!onEdit && !onDelete && !onTest)
            return null;
        return {
            title: "操作",
            key: "defaultActions",
            width: width,
            fixed: "right",
            render: function (_, record) {
                var actions = [];
                if (onEdit) {
                    actions.push(_jsx(Button, { type: "link", size: "small", icon: editIcon || React.createElement(EditOutlined), onClick: function () { return onEdit(record); }, style: { padding: 0, fontSize: "12px" } }, "edit"));
                }
                if (onTest) {
                    actions.push(_jsx(Button, { type: "link", size: "small", icon: testIcon || React.createElement(SettingOutlined), onClick: function () { return onTest(record); }, style: { padding: 0, fontSize: "12px" } }, "test"));
                }
                if (onDelete) {
                    actions.push(_jsx(Button, { type: "link", size: "small", danger: true, icon: deleteIcon || React.createElement(DeleteOutlined), onClick: function () { return onDelete(record); }, style: { padding: 0, fontSize: "12px" } }, "delete"));
                }
                return _jsx(Space, __assign({ size: spacing }, { children: actions }));
            },
        };
    };
    // 获取缺省操作列
    var defaultActionColumn = createDefaultActionColumn();
    // 合并列配置
    var getFinalColumns = function () {
        var baseColumns = resizableColumns ? resizedColumns : visibleColumns;
        // 如果已经有操作列，则不添加缺省操作列
        var hasActionColumn = baseColumns.some(function (col) {
            return col.key === "action" ||
                col.key === "defaultActions" ||
                (typeof col.title === "string" && col.title.includes("操作"));
        });
        if (defaultActionColumn && !hasActionColumn) {
            return __spreadArray(__spreadArray([], baseColumns, true), [defaultActionColumn], false);
        }
        return baseColumns;
    };
    // 构建最终的表格属性
    var finalTableProps = __assign(__assign({}, tableProps), { rowSelection: showSelection
            ? __assign({ selectedRowKeys: selectedRowKeys, onChange: handleSelectionChange }, (tableProps.rowSelection || {})) : undefined, loading: loading, pagination: currentPagination, onChange: handleTableChange, 
        // 使用合并后的列配置
        columns: getFinalColumns(), components: components });
    return (_jsxs(_Fragment, { children: [_jsx("style", { children: "\n          /* \u53EF\u8C03\u6574\u5217\u5BBD\u6837\u5F0F */\n          .react-resizable {\n            position: relative;\n            background-clip: padding-box;\n          }\n\n          .react-resizable-handle {\n            position: absolute;\n            right: 0;\n            top: 0;\n            bottom: 0;\n            width: 8px;\n            height: 100%;\n            cursor: col-resize;\n            z-index: 100;\n            background: transparent;\n          }\n\n          /* \u589E\u5F3A\u60AC\u505C\u6548\u679C */\n          .react-resizable-handle:hover {\n            background-color: rgba(24, 144, 255, 0.15);\n          }\n\n          /* \u62D6\u62FD\u65F6\u663E\u793A\u7684\u8F85\u52A9\u7EBF - \u66F4\u52A0\u660E\u663E */\n          .react-resizable-handle:hover::after,\n          .react-resizable-handle-active::after {\n            content: '';\n            position: absolute;\n            top: 0;\n            bottom: 0;\n            right: 3px;\n            width: 2px;\n            background-color: #1890ff;\n            opacity: 1;\n          }\n\n          /* \u5217\u5934\u5355\u5143\u683C\u6837\u5F0F\u8C03\u6574 */\n          th.ant-table-cell {\n            position: relative !important;\n          }\n        " }), _jsx("div", __assign({ style: {
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                } }, { children: _jsx(Card, __assign({ title: titleWithSettings, extra: combinedExtra, styles: __assign(__assign({}, cardProps.styles), { body: __assign({ padding: "16px", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }, (_b = cardProps.styles) === null || _b === void 0 ? void 0 : _b.body), 
                        // 确保 headerPadding 不被 cardProps.styles 覆盖
                        header: headerPadding
                            ? { padding: headerPadding }
                            : (_c = cardProps.styles) === null || _c === void 0 ? void 0 : _c.header }), style: __assign({ margin: "0", display: "flex", flexDirection: "column", height: "100%" }, cardProps.style) }, cardProps, { children: _jsx("div", __assign({ style: { flex: 1, minHeight: 0 } }, { children: _jsx(Table, __assign({}, finalTableProps)) })) })) }))] }));
}
export default GenericPanelTable;
